import logo from "assets/images/integrations/brevo_logo.png";
import { useCallback, useEffect, useMemo, useState } from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import UpCustomerSettingsButtonFooter from "../../../../../components/UpCustomerSettingsButtonFooter";
import { RootReduxState } from "../../../../../utils/types";
import { selectBox as SelectBox } from "../../../../CustomerSettings/EmailSignature/input-fields";
import { ConnectedIntegrationLineWrapper } from "../../components/ConnectedIntegrationLineWrapper";
import {
  BrevoDataReduxState,
  BrevoRequestReduxState,
  deleteBrevoIntegrationAction,
  getBrevoIntegrationSettingsAction,
  resetBrevoIntegrationsRequestsAction,
  selectBrevoListAction
} from "./redux";

const mapStateToProps = (state: RootReduxState) => {
  const shopId = state.getIn(["customerBackendDashboard", "userData", "selectedShopId"]) as string;

  const selectListRequestState = state
    .getIn(["integrations", "brevo", "requests", "selectList"])
    .toJS() as BrevoRequestReduxState;
  const deleteRequestState = state
    .getIn(["integrations", "brevo", "requests", "delete"])
    .toJS() as BrevoRequestReduxState;

  return {
    shopId: Number(shopId),
    selectListRequestState,
    deleteRequestState,
    integrationData: state.getIn(["integrations", "brevo", "data"])?.toJS() as
      | BrevoDataReduxState
      | undefined
  };
};

const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getBrevoIntegrationSettingsAction,
      deleteBrevoIntegrationAction,
      resetBrevoIntegrationsRequestsAction,
      selectBrevoListAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type BrevoSettingsViewComponentProps = ConnectedProps<typeof connector>;

const BrevoSettingsViewComponent = (
  props: BrevoSettingsViewComponentProps & WrappedComponentProps
) => {
  const {
    integrationData,
    intl,
    selectListRequestState,
    deleteRequestState,
    deleteBrevoIntegrationAction,
    resetBrevoIntegrationsRequestsAction,
    selectBrevoListAction,
    getBrevoIntegrationSettingsAction,
    shopId
  } = props;

  const [selectedListId, setSelectedListId] = useState<number | undefined>(
    integrationData?.settings?.selectedListId
  );
  const [valueChanged, setValueChanged] = useState(false);

  useEffect(
    () => () => {
      resetBrevoIntegrationsRequestsAction();
    },
    []
  );

  useEffect(() => {
    if (selectListRequestState.success) {
      getBrevoIntegrationSettingsAction(shopId);
    }
  }, [selectListRequestState.success]);

  useEffect(() => {
    setSelectedListId(integrationData?.settings?.selectedListId);
    setValueChanged(selectedListId !== integrationData?.settings?.selectedListId);
    resetBrevoIntegrationsRequestsAction();
  }, [integrationData?.settings?.selectedListId]);

  useEffect(() => {
    setValueChanged(selectedListId !== integrationData?.settings?.selectedListId);
  }, [selectedListId]);

  const handleDelete = useCallback(() => {
    deleteBrevoIntegrationAction(shopId);
  }, [shopId]);

  const handleSubmit = useCallback(() => {
    selectBrevoListAction(shopId, selectedListId!);
  }, [shopId, selectedListId]);

  const handleReset = useCallback(() => {
    setSelectedListId(integrationData?.settings?.selectedListId);
  }, [integrationData]);

  const handleListChange = useCallback((value: number) => {
    setSelectedListId(value);
  }, []);

  const listsOptions = useMemo(() => {
    if (integrationData?.settings?.lists === undefined) {
      return [];
    }

    return integrationData.settings.lists.map((l) => ({ label: l.name, value: l.id }));
  }, [integrationData?.settings?.lists]);

  if (integrationData === undefined || integrationData.settings === undefined) {
    return null;
  }

  return (
    <ConnectedIntegrationLineWrapper
      accountName={"Uptain GmbH"}
      deleting={deleteRequestState.loading}
      image={logo}
      name="Brevo"
      onDelete={handleDelete}
    >
      <div>
        <SelectBox
          input={{
            onChange: handleListChange,
            value: selectedListId,
            name: "list"
          }}
          label={intl.formatMessage({
            id: `containers.Integrations.brevo.settings.list.label`,
            defaultMessage: "List for Newsletter-Optins"
          })}
          placeholder="Select List"
          infoText={intl.formatMessage({
            id: `containers.Integrations.brevo.settings.list.info`,
            defaultMessage:
              "After successfully connected to Brevo new optins will be added to this list. After the creation of a new list in the connected Brevo Account, please, delete this configuration line and re-connect again."
          })}
          options={listsOptions}
          emptyMessage="No lists available"
        />
        <UpCustomerSettingsButtonFooter
          saveButtonDisabled={!valueChanged}
          resetButtonDisabled={!valueChanged}
          saveButtonOnClick={handleSubmit}
          resetButtonOnClick={handleReset}
          saving={selectListRequestState.loading || selectListRequestState.success}
        />
      </div>
    </ConnectedIntegrationLineWrapper>
  );
};

export const BrevoSettingsView = connector(injectIntl(BrevoSettingsViewComponent));
